<template>
    <div class="py-12">
        <h1
            class="text-2xl font-medium leading-normal tracking-small text-gray-800"
        >
            Keywords
        </h1>

        <inertia-link :href="route('kiosk.premade-templates.index')">
            <AtomicButton class="mr-2" color="yellow" size="sm">
                Back to Premade Templates
                <template #icon>
                    <BackspaceIcon
                        class="mr-2 h-6 w-6 transition duration-200 group-hover:text-gray-600"
                    />
                </template>
            </AtomicButton>
        </inertia-link>

        <AtomicButton
            class="mb-2 mr-2 mt-4"
            size="sm"
            color="yellow"
            @click="createNew = true"
        >
            Add
            <template #icon>
                <PlusCircleIcon
                    class="mr-2 h-6 w-6 transition duration-200 group-hover:text-gray-600"
                />
            </template>
        </AtomicButton>

        <form
            v-if="createNew"
            class="flex flex-col"
            @submit.prevent="saveKeyword"
        >
            <TextInput
                v-model="v$.form.keyword.$model"
                class="mt-5 w-52 sm:w-80"
                label="Keyword"
                type="text"
                placeholder="max 255 characters"
                :error="getError('keyword')"
                required
            />

            <div class="flex flex-row">
                <AtomicButton
                    type="submit"
                    color="yellow"
                    size="sm"
                    class="mr-2 mt-6"
                    :disabled="loading || v$.$invalid"
                >
                    Save
                </AtomicButton>

                <AtomicButton
                    v-if="createNew"
                    color="white"
                    size="sm"
                    class="mt-6"
                    @click.prevent="handleClose"
                >
                    Cancel
                </AtomicButton>
            </div>
        </form>

        <DataTable
            v-if="keywords.data.length"
            class="mt-10"
            :items="keywords"
            :headers="headers"
        >
            <template #item-custom_edit="{ row }">
                <inertia-link
                    :href="
                        route('kiosk.premade-templates.keywords.edit', {
                            premadeTemplateKeyword: row.id,
                        })
                    "
                >
                    <AtomicButton size="sm" color="white">
                        Edit
                        <template #icon>
                            <PencilSquareIcon
                                class="mr-2 h-6 w-6 transition duration-200 group-hover:text-gray-600"
                            />
                        </template>
                    </AtomicButton>
                </inertia-link>
            </template>

            <template #item-custom_delete="{ row }">
                <AtomicButton size="sm" color="red" @click="deleteKeyword(row)">
                    <template #icon>
                        <TrashIcon
                            class="h-6 w-6 transition duration-200 group-hover:text-gray-600"
                        />
                    </template>
                </AtomicButton>
            </template>
        </DataTable>
    </div>
</template>

<script lang="ts" setup>
import AtomicButton from '@atomic/Buttons/AtomicButton.vue';
import TextInput from '@atomic/Inputs/TextInput.vue';

import { route } from 'ziggy-js';
import {
    PlusCircleIcon,
    PencilSquareIcon,
    TrashIcon,
    BackspaceIcon,
} from '@heroicons/vue/24/outline';
import IPaginationData from '@/components/Atomic/DataTable/types/IPaginationData';
import { router } from '@inertiajs/vue3';
import { reactive, ref } from 'vue';
import DataTable from '@/components/Atomic/DataTable/DataTable.vue';
import { create } from '@/composables/useNotifications';
import IDataTableHeader from '@/components/Atomic/DataTable/types/IDataTableHeader';
import useConfirm from '@/composables/useConfirm';
import IKeyword from '@/types/Templates/IKeyword';
import { required } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import IInputErrors from '@/types/Atomic/IInputErrors';

interface IError {
    keyword?: string;
}

defineProps<{
    errors: IError;
    keywords: IPaginationData<IKeyword>;
}>();

const createNew = ref(false);

const loading = ref(false);
const { show } = useConfirm();

const inputErrors = ref<IInputErrors>({});

const state = reactive({
    form: {
        keyword: '',
    },
});

const rules = {
    form: {
        keyword: { required },
    },
};

const v$ = useVuelidate(rules, state);

const headers: IDataTableHeader[] = [
    {
        text: 'ID',
        id: 'id',
        sortable: true,
        sortableId: 'id',
    },
    {
        text: 'Keyword',
        id: 'keyword',
        sortable: true,
        sortableId: 'keyword',
    },
    { text: '', id: 'custom_edit' },
    { text: '', id: 'custom_delete' },
];

const saveKeyword = () => {
    v$.value.form.keyword.$touch();
    if (v$.value.form.keyword.$error) {
        return;
    } else {
        loading.value = true;
        router.post(
            route('kiosk.premade-templates.keywords.store'),
            state.form,
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'New keyword created',
                        type: 'success',
                    });
                    createNew.value = false;
                    state.form.keyword = '';
                    inputErrors.value = {};
                    v$.value.$reset();
                },
                onError: (err) => {
                    inputErrors.value = err;
                    create({
                        title: 'Keyword not created',
                        type: 'error',
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            }
        );
    }
};

const deleteKeyword = async (keyword: IKeyword) => {
    const confirmed = await show({
        header: 'Delete premade keyword',
        message: `Are you sure you want to delete keyword ${keyword.keyword}?`,
        type: 'danger',
        confirm: 'Delete keyword',
    });
    if (confirmed) {
        router.delete(
            route('kiosk.premade-templates.keywords.delete', {
                premadeTemplateKeyword: keyword.id,
            }),
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'Keyword deleted',
                        type: 'success',
                    });
                },
                onError: () => {
                    create({
                        title: 'Keyword not deleted',
                        type: 'error',
                    });
                },
            }
        );
    }
};

const getError = (field: string) => {
    return (
        v$.value.form[field]?.$errors[0]?.$message ||
        inputErrors.value?.[field] ||
        ''
    );
};

const handleClose = () => {
    v$.value.$reset();
    state.form.keyword = '';
    createNew.value = false;
    inputErrors.value = {};
};
</script>

<style></style>
